import React, { useCallback } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { ActionDock, SecondaryButton } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import RunStopsList from './components/RunStopsList';
import { useDriverDay } from './DriverDayProvider';
import { DRIVER_ROUTE } from './driverRoutes';
import StartOfDayStepScreen from './startOfDay/StartOfDayStepScreen';
import useStartOfDayStep from './startOfDay/useStartOfDayStep';
import useDriverDayActions from './useDriverDayActions';

const countLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

/**
 * Run overview (start of day step): today's stops in route order grouped by
 * site (the run list's RunStopsList), numbered and view-only (stops open from
 * On the run), with Route map and Reorder stops (the run list's reorder
 * screen), confirmed with "Run overview looks right".
 */
const RunOverviewView = () => {
	const styles = useThemedStyles(createStyles);
	const navigation = useNavigation();
	const day = useDriverDay();
	const {
		sortedDockets,
		isDocketDone,
		reorderableStops,
		trainingOffline,
	} = day;
	const { isDone, stepLabel, afterThis } = useStartOfDayStep(day, 'runOverview');
	const {
		openFullRouteMap,
		requireTrainingConnection,
		confirmStepByKey,
		isConfirmingStep,
	} = useDriverDayActions(day);

	const openReorder = useCallback(() => {
		if (requireTrainingConnection()) {
			navigation.push(DRIVER_ROUTE.REORDER_STOPS);
		}
	}, [navigation, requireTrainingConnection]);

	return (
		<StartOfDayStepScreen
			day={day}
			stepLabel={stepLabel}
			heading="Run overview"
			subheading={`${countLabel(sortedDockets.length, 'stop', 'stops')} in today's order. Check it makes sense before you leave.`}
			dock={isDone ? (
				<ActionDock hint="Run overview confirmed." />
			) : (
				<ActionDock
					hint={afterThis}
					action={{
						label: 'Run overview looks right',
						onPress: () => confirmStepByKey('runOverview'),
						loading: isConfirmingStep,
						disabled: trainingOffline,
					}}
				/>
			)}
		>
			<View style={styles.buttons}>
				<SecondaryButton label="Route map" icon="map" onPress={openFullRouteMap} style={styles.button} />
				{reorderableStops.length > 1 && (
					<SecondaryButton
						label="Reorder stops"
						icon="shuffle"
						onPress={openReorder}
						disabled={trainingOffline}
						style={styles.button}
					/>
				)}
			</View>
			<RunStopsList
				dockets={sortedDockets}
				isDocketDone={isDocketDone}
				showStopNumbers={true}
			/>
		</StartOfDayStepScreen>
	);
};

const createStyles = ({ ui: { space } }) => ({
	buttons: {
		flexDirection: 'row',
		gap: space.sm,
	},
	button: {
		flex: 1,
	},
});

export default RunOverviewView;
